import React from 'react';
import Card from "components/card";
import { MdAnalytics, MdAssessment, MdArrowForward, MdPieChart, MdTrendingUp } from "react-icons/md";
import { useNavigate } from 'react-router-dom';

const AnalysesEntryCard = () => {
  const navigate = useNavigate();

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <div className="mt-2 sm:mt-5 h-full flex justify-center px-2 sm:px-4 lg:px-6">
      <div className="w-full max-w-6xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-navy-700 dark:text-white mb-3">
            Product Analysis Entry
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
            Choose the type of analysis you want to perform. Select between pieces analysis for detailed quality assessment or wholes analysis for comprehensive product evaluation.
          </p>
        </div>

        {/* Cards Container */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          
          {/* Pieces Analysis Card */}
          <div 
            onClick={() => handleCardClick('/admin/analyst-process/entry1')}
            className="group cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
          >
            <Card extra="relative overflow-hidden h-full">
              {/* Gradient Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
              
              {/* Card Content */}
              <div className="relative z-10 p-6 sm:p-8">
                {/* Icon and Badge */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg">
                    <MdPieChart className="text-3xl text-white" />
                  </div>
                  <div className="bg-blue-100 dark:bg-blue-900 px-3 py-1 rounded-full">
                    <span className="text-blue-700 dark:text-blue-300 text-xs font-semibold">PIECES</span>
                  </div>
                </div>

                {/* Title and Description */}
                <div className="mb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-navy-700 dark:text-white mb-3">
                    Pieces Analysis
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base leading-relaxed">
                    Detailed analysis of individual pieces including sieve analysis, quality assessment, and defect identification for precise quality control.
                  </p>
                </div>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    Sieve analysis and quality grading
                  </div>
                  <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                    Defect identification and classification
                  </div>
                  <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                    Moisture content measurement
                  </div>
                  <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
                    Comprehensive quality reporting
                  </div>
                </div>

                {/* Action Button */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-blue-600 dark:text-blue-400 font-semibold group-hover:text-blue-700 transition-colors">
                    <span className="mr-2">Start Analysis</span>
                    <MdArrowForward className="text-lg transform group-hover:translate-x-1 transition-transform" />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Click to begin
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
              <div className="absolute bottom-4 left-4 w-16 h-16 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
            </Card>
          </div>

          {/* Wholes Analysis Card */}
          <div 
            onClick={() => handleCardClick('/admin/analyst-process/entry2')}
            className="group cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl"
          >
            <Card extra="relative overflow-hidden h-full">
              {/* Gradient Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-teal-500 to-green-600 opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
              
              {/* Card Content */}
              <div className="relative z-10 p-6 sm:p-8">
                {/* Icon and Badge */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg">
                    <MdTrendingUp className="text-3xl text-white" />
                  </div>
                  <div className="bg-emerald-100 dark:bg-emerald-900 px-3 py-1 rounded-full">
                    <span className="text-emerald-700 dark:text-emerald-300 text-xs font-semibold">WHOLES</span>
                  </div>
                </div>

                {/* Title and Description */}
                <div className="mb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-navy-700 dark:text-white mb-3">
                    Wholes Analysis
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base leading-relaxed">
                    Comprehensive quality assessment focusing on First, Second, Third grades and overall product quality evaluation for wholes classification.
                  </p>
                </div>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                    First, Second, Third grade classification
                  </div>
                  <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                    <div className="w-2 h-2 bg-teal-500 rounded-full mr-3"></div>
                    Spotted and very spotted assessment
                  </div>
                  <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    W3M and dessert quality evaluation
                  </div>
                  <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full mr-3"></div>
                    Wholes quality certification
                  </div>
                </div>

                {/* Action Button */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-emerald-600 dark:text-emerald-400 font-semibold group-hover:text-emerald-700 transition-colors">
                    <span className="mr-2">Start Analysis</span>
                    <MdArrowForward className="text-lg transform group-hover:translate-x-1 transition-transform" />
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Click to begin
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
              <div className="absolute bottom-4 left-4 w-16 h-16 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
            </Card>
          </div>
        </div>

        {/* Bottom Info Section */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-navy-800 rounded-full">
            <MdAnalytics className="text-gray-600 dark:text-gray-400 mr-2" />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Choose the appropriate analysis type based on your quality control requirements
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysesEntryCard; 